<script setup>
import { ref, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();

let isOpened = ref(false);
let openSettingTimeout = null;
function setOpen() {
  openSettingTimeout = setTimeout(() => {
    isOpened.value = true;
  }, 500);
}
function setClosed() {
  clearTimeout(openSettingTimeout);
  isOpened.value = false;
}
</script>

<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.SERVICES.OPTIONS.SELECTED"
    :handler="
      async () => {
        await $nextTick();
        await $nextTick();
        await $nextTick();
        $stores.services?.state?.transactionInitiation?.service?.id ==
          'giftCard' &&
          $stores.services?.state?.transactionInitiation?.operation?.id ==
            'sell' &&
          $refs.modal.show();
        setOpen();
      }
    "
  />
  <VBusHelper
    :reactOn="$constants.EVENTS.SERVICES.CLOSE_OPERATION_INITIATION"
    :handler="() => $refs.modal.hide()"
  />
  <DialogComponent
    ref="modal"
    type="bottomsheet"
    noInnerPadding
    noOverlay
    hideCloseButton
    :mainClass="
      ({ isLastDialog }) =>
        isLastDialog
          ? '[&_.formContainer]:duration-300'
          : '[&_.formContainer]:opacity-0 [&.formContainer]:duration-300'
    "
    @closed="
      ({ source }) => {
        source !== 'external' &&
          $bus.emit($constants.EVENTS.SERVICES.CLOSE_SERVICE_INITIATION);
        setClosed();
      }
    "
  >
    <Transition
      enter-active-class="transform transition ease-out duration-300"
      enter-from-class="-translate-y-20 opacity-0"
      enter-to-class="scale-100 opacity-100"
      leave-active-class="transform transition duration-400"
      leave-from-class="scale-100 opacity-100"
      leave-to-class="scale-120 opacity-0"
    >
      <div
        v-if="isOpened"
        class="fixed top-0 left-0 w-full h-full flex flex-col bg-white"
      >
        <div
          :style="{
            backgroundColor:
              $stores.services.state?.transactionInitiation?.service?.color
          }"
          class="opacity-10 absolute w-full h-full top-0 left-0"
        ></div>
        <div class="absolute w-full h-full">
          <div
            class="h-[60%] flex items-center justify-center pb-[40%] relative"
            :style="{
              backgroundColor:
                $stores.services.state?.transactionInitiation?.service?.color
            }"
          >
            <div class="absolute w-full h-full top-0 left-0 bg-white/40">
              <ImgComponent
                src="/images/backgrounds/bg1.svg"
                class="w-full h-full object-cover"
                style="mix-blend-mode: soft-light"
                alt=""
              />
            </div>
            <ImgComponent
              :src="
                $stores.services.state?.transactionInitiation?.service
                  ?.bannerImage
              "
              class="w-[90%]"
              alt=""
            />
          </div>
        </div>
      </div>
    </Transition>

    <div
      v-if="isOpened"
      class="fixed rounded-full w-10 h-10 top-4 right-4 mobile-status-padding-mt"
      :style="{
        backgroundColor:
          $stores.services.state?.transactionInitiation?.service?.color
      }"
      @click="() => $refs.modal.hide('background')"
    >
      <ColorImg
        src="/images/icons/close.svg"
        class="bg-white rounded-full"
        alt=""
      />
    </div>

    <div
      v-if="isOpened"
      class="fixed w-20 h-20 top-[11vh] left-1/2 -translate-x-1/2 z-2 flex items-center justify-center cursor-pointer group"
      @click="
        $bus.emit(
          $constants.EVENTS.VIDEO.PLAY,
          $stores.appData?.state?.data?.general?.specificTutorials?.[
            $stores.services?.state?.transactionInitiation?.service?.id
          ]?.withdrawVideoLink ||
            $stores.appData?.state?.data?.general?.withdrawVideoLink
        )
      "
    >
      <!-- Ping background -->
      <div
        class="absolute w-18 h-18 bg-white rounded-full animate-ping opacity-75"
      ></div>

      <!-- Play button inner -->
      <div
        class="relative w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/40 shadow-2xl transition-all duration-300 group-hover:scale-110 group-hover:bg-white/30"
      >
        <svg
          class="w-10 h-10 text-white"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M8 5.14v14l11-7-11-7z" />
        </svg>
      </div>
    </div>

    <!-- Header avec dégradé (Ajouté pour cohérence) -->
    <div
      :style="{
        background: `linear-gradient(135deg, ${$stores.services.state?.transactionInitiation?.service?.color}dd, ${$stores.services.state?.transactionInitiation?.service?.color}cc)`
      }"
      class="relative rounded-t-2xl overflow-hidden pb-8 pt-2 -mb-5 formContainer"
    >
      <!-- Ornement décoratif -->
      <div
        class="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"
      ></div>
      <div
        class="absolute bottom-0 left-0 w-32 h-32 bg-black/10 rounded-full translate-y-1/2 -translate-x-1/2"
      ></div>

      <div class="pb-10 px-6 pt-6 -mb-6 relative z-10">
        <div class="grid grid-cols-d5 items-center gap-4">
          <div class="col-span-4">
            <div class="text-white font-extrabold text-3xl tracking-tight">
              {{ $stores.services.state?.transactionInitiation?.service?.name }}
            </div>
            <div class="text-white/90 text-base font-medium mt-1">
              {{
                $stores.services.state?.transactionInitiation?.operation
                  ?.description
              }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Formulaire (Fond gris cohérent) -->
    <div
      class="flex flex-col gap-6 text-gray-700 p-6 bg-gray-50 rounded-t-3xl relative z-2"
    >
      <form
        class="w-full grid grid-cols-12 gap-4"
        v-bus:EVENTS.SERVICES.INITIATION.SUBMIT_FORM.submit.prevent
      >
        <!-- Container principal pour tous les champs -->
        <div
          class="col-span-12 bg-white rounded-3xl shadow-lg shadow-gray-200/50 divide-y divide-gray-100 overflow-hidden [&_input,&_select]:text-gray-800 [&_input,&_select]:font-semibold"
        >
          <!-- Type de carte -->
          <label
            class="grid grid-cols-5 py-4 divide-x divide-gray-100 items-center group hover:bg-gray-50/50 transition-colors"
          >
            <div class="flex flex-col px-5 col-span-4">
              <label
                class="mb-2 px-1 text-xs font-bold text-gray-400 uppercase tracking-wider"
                >Type de carte</label
              >
              <select
                class="w-full px-1 text-lg bg-transparent"
                v-model="$stores.services.state.transactionInitiation.cardType"
              >
                <option value="amazon">Amazon</option>
                <option value="netflix">Netflix</option>
                <option value="spotify">Spotify</option>
                <option value="itunes">iTunes</option>
                <option value="google">Google Play</option>
              </select>
            </div>
            <div class="p-6 flex items-center justify-center">
              <div
                class="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-gray-100"
              >
                <ColorImg
                  :style="{
                    backgroundColor: `${$stores.services?.state?.transactionInitiation?.service?.color}88`
                  }"
                  src="/images/formIcons/gift.png"
                  alt=""
                  class="w-8 h-8"
                />
              </div>
            </div>
          </label>

          <!-- Montant -->
          <label
            class="grid grid-cols-5 py-4 divide-x divide-gray-100 items-center group hover:bg-gray-50/50 transition-colors"
          >
            <div class="flex flex-col px-5 relative col-span-4">
              <label
                class="mb-2 px-1 text-xs font-bold text-gray-400 uppercase tracking-wider"
                >Montant</label
              >
              <div class="flex">
                <input
                  v-model="
                    $stores.services.state.transactionInitiation.recipientAmount
                  "
                  type="number"
                  class="w-full px-1 text-lg"
                  required
                />
                <div class="flex justify-end left-0">
                  <div
                    class="text-amber-600 bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-1.5 rounded-xl whitespace-nowrap text-sm font-bold shadow-sm"
                    :class="{
                      shimmerElement:
                        $stores.services.state.transactionInitiationFees
                          ?.loadingFees
                    }"
                  >
                    <span class="mr-1">🔥</span>
                    Bonus:
                    {{
                      $stores.services.state.transactionInitiationFees?.bonus ||
                      0
                    }}
                    FCFA
                  </div>
                </div>
              </div>
              <small class="text-gray-500 mt-1 px-1">Minimum : 500 CFA</small>
            </div>
            <div class="p-6 flex items-center justify-center">
              <div
                class="min-w-14 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-gray-100"
              >
                <ColorImg
                  :style="{
                    backgroundColor: `${$stores.services?.state?.transactionInitiation?.service?.color}88`
                  }"
                  src="/images/formIcons/money.png"
                  alt=""
                  class="w-8 h-8"
                />
              </div>
            </div>
            <div class="col-span-5">
              <div
                v-if="sellAmountError"
                class="px-5 py-3 bg-rose-50 border border-rose-100 rounded-2xl flex items-start gap-3 mx-4 mb-4"
              >
                <svg
                  class="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div class="text-sm font-medium text-rose-800 leading-tight">
                  {{ sellAmountError }}
                </div>
              </div>
            </div>
          </label>

          <!-- Mode de paiement -->
          <div class="col-span-12 flex flex-col">
            <label class="mb-1">Mode de retrait</label>
            <select
              class="p-4 border rounded-xl border-neutral-200 focus:border-primary-400 w-full"
              v-model="
                $stores.services.state.transactionInitiation.paymentMethod
              "
            >
              <option
                v-for="(option, i) in $constants.SERVICES.PAYMENT_METHODS"
                :value="option"
              >
                {{ option.name }}
              </option>
            </select>
          </div>

          <!-- Numéro de téléphone -->
          <div class="col-span-12 flex flex flex-col">
            <label class="mb-1">Téléphone</label>
            <div class="relative flex items-center">
              <input
                v-model="
                  $stores.services.state.transactionInitiation.phoneNumber
                "
                required
                type="text"
                class="p-4 border rounded-xl border-neutral-200 focus:border-primary-400 w-full"
                placeholder="Ex: 61020304"
                ref="phoneNumberInput"
              />
              <div class="absolute right-4">
                <PhoneDetail
                  :phoneNumber="
                    $stores.services.state.transactionInitiation.phoneNumber
                  "
                  :countryCode="$stores.user.state.current?.countryCode"
                  @loading="
                    (data) => {
                      $refs.phoneNumberInput.setCustomValidity('chargement');
                      $stores.services.state.transactionInitiation.phoneOperator =
                        '';
                    }
                  "
                  @resolved="
                    (data) => {
                      $refs.phoneNumberInput.setCustomValidity('');
                      $stores.services.state.transactionInitiation.phoneOperator =
                        data?.carrier;
                    }
                  "
                  @error="
                    (data) => {
                      $stores.services.state.transactionInitiation.phoneOperator =
                        '';
                      $refs.phoneNumberInput.setCustomValidity(
                        `Erreur du numéro: ${data?.error}`
                      );
                    }
                  "
                  v-slot="{ carrier } = {}"
                >
                  {{ carrier }}
                </PhoneDetail>
              </div>
            </div>
          </div>

          <!-- Code de la carte -->
          <div class="col-span-12 flex flex-col">
            <label class="mb-1">Code de la carte</label>
            <input
              v-model="$stores.services.state.transactionInitiation.cardCode"
              type="text"
              class="p-4 border rounded-xl border-neutral-200 focus:border-primary-400 w-full"
              placeholder="Entrez le code de la carte"
            />
          </div>
        </div>

        <!-- Bouton résumé -->
        <div class="col-span-12 flex flex-col gap-4 mt-4">
          <div
            class="p-5 rounded-2xl text-white shadow-lg relative overflow-hidden"
            :style="{
              background: `linear-gradient(135deg, ${$stores.services?.state?.transactionInitiation?.service?.color}dd, ${$stores.services?.state?.transactionInitiation?.service?.color}cc)`
            }"
          >
            <div
              class="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"
            ></div>
            <div class="relative z-10 flex items-center gap-2">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center"
              >
                <ColorImg
                  class="w-8 h-8 bg-white/50"
                  src="/images/icons/idea.png"
                  alt=""
                />
              </div>
              <span class="font-semibold text-white/90"
                >Nous allons vendre des cartes cadeaux</span
              >
            </div>
          </div>

          <!-- Bouton Continuer -->
          <button
            class="w-full flex items-center justify-center gap-3 py-4 px-6 text-white font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            :style="{
              background: `linear-gradient(135deg, ${$stores.services.state?.transactionInitiation?.service.color}ee, ${$stores.services.state?.transactionInitiation?.service.color}cc)`
            }"
            type="submit"
          >
            <span class="relative z-10 flex items-center justify-center gap-2">
              <span>Continuer</span>
              <svg
                class="w-6 h-6 group-hover:translate-x-1 transition-transform relative z-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                ></path>
              </svg>
            </span>
          </button>

          <button
            v-if="$stores.services?.state?.initiatingPayment"
            type="button"
            @click="
              $bus.emit($constants.EVENTS.SERVICES.CLOSE_SERVICE_INITIATION)
            "
            class="w-full py-3 mt-2 text-red-600 font-bold border-2 border-red-100 rounded-xl hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
          >
            Annuler la transaction
          </button>

          <button
            @click="() => $refs.modal.hide('manual')"
            type="button"
            class="w-full py-4 px-6 font-semibold rounded-2xl border-2 transition-all duration-300 flex items-center justify-center gap-3 hover:bg-gray-50 active:scale-[0.98]"
            :style="{
              borderColor:
                $stores.services.state?.transactionInitiation?.service.color,
              color:
                $stores.services.state?.transactionInitiation?.service.color
            }"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              ></path>
            </svg>
            Retour
          </button>
        </div>
      </form>
    </div>
  </DialogComponent>
</template>
